import { useEffect, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { LogOut } from 'lucide-react'

const navItems = [
  { to: '/apps', icon: 'view_quilt', label: 'My Apps', filled: true },
  { to: '/settings', icon: 'settings', label: 'Account Settings', filled: false },
  { to: '/analytics', icon: 'monitoring', label: 'Analytics', filled: false },
]

export function Layout() {
  const { user, signOut } = useAuth()
  const location = useLocation()

  // Trên điện thoại sidebar là ngăn kéo; máy tính (md trở lên) thì luôn hiện.
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Đổi trang thì đóng ngăn kéo, nếu không nó che mất nội dung vừa mở.
  useEffect(() => setDrawerOpen(false), [location.pathname])

  // Khoá cuộn nền khi ngăn kéo đang mở.
  useEffect(() => {
    if (!drawerOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [drawerOpen])

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-md px-md py-sm font-label-md text-[14px] rounded-lg transition-all ${
      isActive
        ? 'bg-secondary-container text-on-secondary-container'
        : 'text-on-surface-variant hover:bg-surface-container-high'
    }`

  return (
    <div className="bg-surface text-on-surface flex min-h-screen">
      {/* Nền mờ: chỉ có trên điện thoại khi ngăn kéo mở */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed left-0 top-0 h-full w-64 bg-surface-container-low border-r border-outline-variant p-md flex flex-col z-50 transition-transform duration-200 ease-in-out ${
          drawerOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0`}
      >
        <div className="mb-xl flex items-center gap-sm">
          <div className="w-10 h-10 bg-primary-container rounded-lg flex items-center justify-center text-on-primary-container shrink-0">
            <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>apps</span>
          </div>
          <div className="min-w-0">
            <h1 className="font-headline-sm text-[18px] font-bold text-primary leading-tight">MTSubKit</h1>
            <p className="font-label-md text-[13px] text-on-surface-variant truncate">Management Portal</p>
          </div>
          <button
            className="ml-auto p-2 rounded-full text-on-surface-variant hover:bg-surface-container-high md:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-label="Đóng menu"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <nav className="flex-1 space-y-1 mt-6">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} className={linkClass}>
              <span
                className="material-symbols-outlined"
                style={item.filled ? { fontVariationSettings: "'FILL' 1" } : undefined}
              >
                {item.icon}
              </span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto pt-md border-t border-outline-variant space-y-1">
          <button
            onClick={() => void signOut()}
            className="w-full flex items-center gap-md px-md py-sm font-label-md text-[14px] text-on-surface-variant hover:bg-surface-container-highest transition-all rounded-lg text-left"
          >
            <LogOut size={20} className="text-on-surface-variant" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* `min-w-0` bắt buộc: thiếu nó, một bảng rộng trong flex item sẽ nong cả
          trang ra thay vì tự cuộn ngang bên trong. */}
      <main className="flex-1 min-w-0 md:ml-64 flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 bg-surface-container-lowest border-b border-outline-variant flex justify-between items-center px-md md:px-lg h-16 w-full">
          <div className="flex items-center gap-sm min-w-0">
            <button
              className="p-2 -ml-2 rounded-full text-on-surface-variant hover:bg-surface-container-low md:hidden"
              onClick={() => setDrawerOpen(true)}
              aria-label="Mở menu"
            >
              <span className="material-symbols-outlined">menu</span>
            </button>
            <span className="font-label-md text-[15px] font-bold text-primary md:hidden">MTSubKit</span>
          </div>
          <div className="flex items-center gap-md">
            <div className="h-8 w-8 rounded-full bg-secondary-fixed overflow-hidden border border-outline-variant flex items-center justify-center font-bold text-on-secondary-container text-sm shrink-0">
              {user?.email?.charAt(0).toUpperCase()}
            </div>
          </div>
        </header>

        <div className="p-md md:p-xl max-w-7xl w-full mx-auto flex-1 min-w-0">
          <Outlet />
        </div>

        <footer className="flex flex-wrap gap-x-lg gap-y-1 items-center px-md md:px-lg mt-auto w-full py-md border-t border-outline-variant bg-surface">
          <span className="font-label-md text-[14px] font-bold text-on-surface">MTSubKit</span>
          <span className="font-label-sm text-[12px] text-on-surface-variant">© 2024 MTSubKit Systems Inc.</span>
        </footer>
      </main>
    </div>
  )
}
